/** The files staged for the question being written: each uploads on its own
 *  as soon as it is added (paste, drop or pick), so by the time the operator
 *  presses Diagnose only ids remain to be sent.
 *
 *  Caps come from the launch context. A file over the size cap, an empty
 *  file, or one past the per-question / per-thread count is refused up front
 *  (the caller shows why) — it never becomes a chip. */
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import {
  uploadAttachment,
  type DiagnoseAttachment,
  type DiagnoseAttachmentCaps,
} from '@/api/diagnoseAttachments';
import { formatBytes } from '@/utils/formatBytes';

export interface StagedAttachment {
  localId: string;
  file: File;
  /** `blob:` thumbnail for images; revoked when the chip goes away. */
  previewUrl: string | null;
  state: 'uploading' | 'ready' | 'error';
  /** 0..1 while uploading. */
  progress: number;
  descriptor?: DiagnoseAttachment;
  error?: string;
}

export interface AttachmentStaging {
  items: StagedAttachment[];
  add: (files: File[]) => void;
  remove: (localId: string) => void;
  retry: (localId: string) => void;
  /** Forget everything (after a successful start, or a new thread). */
  reset: () => void;
  uploading: boolean;
  hasErrors: boolean;
  /** Descriptors of the uploaded files, in the order they were added. */
  ready: DiagnoseAttachment[];
}

let seq = 0;
const nextId = () => `att-${Date.now().toString(36)}-${(seq += 1)}`;

function uploadError(e: unknown): string {
  if (e instanceof ConsoleApiError) {
    if (e.status === 413) return 'Too large';
    if (e.status === 403 && e.body?.code === 'attachments_disabled') return 'Attachments are disabled';
    return e.body?.detail ?? e.message;
  }
  return (e as Error)?.message || 'Upload failed';
}

export function useAttachmentStaging({
  caps,
  alreadyInThread,
  onRejected,
}: {
  caps: DiagnoseAttachmentCaps | undefined;
  /** Files the open thread already carries (counts toward max_per_thread). */
  alreadyInThread: number;
  /** Why some of the added files were refused. */
  onRejected: (message: string) => void;
}): AttachmentStaging {
  const [items, setItems] = useState<StagedAttachment[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const controllers = useRef(new Map<string, AbortController>());

  const upload = useMutation({
    mutationFn: (v: { file: File; signal: AbortSignal; onProgress: (f: number) => void }) =>
      uploadAttachment(v.file, { signal: v.signal, onProgress: v.onProgress }),
  });
  const { mutateAsync } = upload;

  const patch = useCallback((localId: string, change: Partial<StagedAttachment>) => {
    setItems((list) => list.map((it) => (it.localId === localId ? { ...it, ...change } : it)));
  }, []);

  const start = useCallback(
    (localId: string, file: File) => {
      const ctrl = new AbortController();
      controllers.current.set(localId, ctrl);
      patch(localId, { state: 'uploading', progress: 0, error: undefined });
      mutateAsync({ file, signal: ctrl.signal, onProgress: (progress) => patch(localId, { progress }) })
        .then((descriptor) => patch(localId, { state: 'ready', progress: 1, descriptor }))
        .catch((e: unknown) => {
          if (ctrl.signal.aborted) return; // removed while uploading
          patch(localId, { state: 'error', error: uploadError(e) });
        })
        .finally(() => controllers.current.delete(localId));
    },
    [mutateAsync, patch],
  );

  const add = useCallback(
    (files: File[]) => {
      if (!caps?.enabled || files.length === 0) return;
      const refused: string[] = [];
      const accepted: File[] = [];
      const current = itemsRef.current.length;
      for (const f of files) {
        if (f.size === 0) refused.push(`${f.name || 'A file'} is empty.`);
        else if (f.size > caps.max_file_bytes) {
          refused.push(`${f.name} is larger than the ${formatBytes(caps.max_file_bytes)} limit.`);
        } else if (current + accepted.length >= caps.max_per_turn) {
          refused.push(`At most ${caps.max_per_turn} files per question.`);
        } else if (alreadyInThread + current + accepted.length >= caps.max_per_thread) {
          refused.push(`This thread has reached its ${caps.max_per_thread}-file limit.`);
        } else accepted.push(f);
      }
      if (refused.length > 0) onRejected([...new Set(refused)].join(' '));
      if (accepted.length === 0) return;
      const staged = accepted.map<StagedAttachment>((file) => ({
        localId: nextId(),
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        state: 'uploading',
        progress: 0,
      }));
      setItems((list) => [...list, ...staged]);
      staged.forEach((s) => start(s.localId, s.file));
    },
    [caps, alreadyInThread, onRejected, start],
  );

  const remove = useCallback((localId: string) => {
    controllers.current.get(localId)?.abort();
    controllers.current.delete(localId);
    setItems((list) => {
      const gone = list.find((it) => it.localId === localId);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return list.filter((it) => it.localId !== localId);
    });
  }, []);

  const retry = useCallback(
    (localId: string) => {
      const it = itemsRef.current.find((x) => x.localId === localId);
      if (it && it.state === 'error') start(localId, it.file);
    },
    [start],
  );

  const reset = useCallback(() => {
    controllers.current.forEach((c) => c.abort());
    controllers.current.clear();
    itemsRef.current.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    setItems([]);
  }, []);

  // Unmount: abort uploads still in flight and release the thumbnails.
  useEffect(
    () => () => {
      controllers.current.forEach((c) => c.abort());
      itemsRef.current.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    },
    [],
  );

  const ready = useMemo(
    () => items.filter((it) => it.state === 'ready' && it.descriptor).map((it) => it.descriptor as DiagnoseAttachment),
    [items],
  );

  return {
    items,
    add,
    remove,
    retry,
    reset,
    uploading: items.some((it) => it.state === 'uploading'),
    hasErrors: items.some((it) => it.state === 'error'),
    ready,
  };
}
